import { useRouter } from 'next/router';
import Form from 'react-bootstrap/Form';
import styles from './languageSwitcher.module.scss';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import handleLanguageChange from '@/utilites/handle-language-change';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import languageConstants from '@/constants/language.constants';
import Image from 'next/image';
import { useEffect, useRef } from 'react';
import { UserDTO } from '@/types/other';
import { FaChevronDown } from 'react-icons/fa';
import useBackendUser from '@/hooks/useBackendUser';
export default function LanguageSwitcher({
  isHeader,
  isResetPassword,
}: {
  isHeader?: boolean;
  isResetPassword?: boolean;
}) {
  const { data: user } = useBackendUser();
  const queryClient = useQueryClient();
  const disabledOptionRef = useRef<HTMLSelectElement>(null);
  const router = useRouter();
  const selectedLanguage =
    router.locale === router.defaultLocale ? 'en' : router.locale;
  const { t: tCommon } = useTranslation('common');
  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    await handleLanguageChange(
      e.target.value,
      tCommon,
      router,
      Boolean(user),
      queryClient
    );
  };

  useEffect(() => {
    if (disabledOptionRef.current) {
      disabledOptionRef.current.classList.add('tw-hidden');
    }
  }, []);

  if (isResetPassword) {
    return (
      <div className="tw-relative">
        <select className="tw-bg-[#FFFFFF] tw-text-[#64748B] tw-font-bold tw-py-4 tw-pl-4 tw-pr-7 tw-text-lg tw-border-none tw-outline-none tw-appearance-none tw-rounded-xl max-md:tw-text-xs">
          <option value="en">English (EN)</option>
          <option value="de">German (DE)</option>
          <option value="fr">French (FR)</option>
          <option value="it">Italian (IT)</option>
          <option value="es">Spanish (ES)</option>
        </select>
        <FaChevronDown className="tw-absolute tw-top-2/4 -tw-translate-y-2/4 tw-right-4 tw-text-[#64748B] tw-font-bold tw-text-base" />
      </div>
    );
  }

  if (isHeader) {
    return (
      <div className="tw-relative tw-ms-4 !tw-w-20 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-xl">
        <Image
          src={
            Object.values(languageConstants.localesWithIcons).find(
              (locale) => locale.locale === selectedLanguage
            )?.icon.src ?? ''
          }
          alt={selectedLanguage ?? ''}
          width={20}
          height={20}
          className="tw-mr-4 tw-shadow-sm"
        />
        <Form.Select
          onChange={handleChange}
          value={selectedLanguage}
          title="Language"
          className="tw-h-full !tw-text-transparent !tw-bg-transparent !tw-border-0 tw-absolute tw-left-2/4 tw-top-2/4 tw--translate-x-2/4 tw--translate-y-2/4"
        >
          <option disabled value="">
            {tCommon('select_language')}&nbsp;&nbsp;&nbsp;
          </option>
          {Object.values(languageConstants.localesWithIcons).map(
            (locale, index) => {
              return (
                <option
                  className="tw-text-black tw-pr-3"
                  key={`lang-${index}-item-${locale}`}
                  value={locale.locale}
                >
                  {tCommon(locale.tCommonKey)}
                </option>
              );
            }
          )}
        </Form.Select>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <Form.Select
        className={`${styles.languageSwitcher}`}
        onChange={handleChange}
        value={selectedLanguage}
      >
        <option value="en">EN</option>
        <option value="de">DE</option>
        <option value="fr">FR</option>
        <option value="it">IT</option>
        <option value="es">ES</option>
      </Form.Select>
      <div className={styles.globeLang}>
        <FontAwesomeIcon icon={faGlobe} />
      </div>
    </div>
  );
}
